import { useState } from 'react';
import {
  Text,
  Flex,
  Icon,
  Collapsible,
  CollapsibleTrigger,
  CollapsibleContent,
  cn,
} from '@kognitos/lattice';

/**
 * Props for the VersionHistoryDescription component.
 */
interface IVersionHistoryDescriptionProps {
  title: string;
  details?: string;
}

/**
 * Displays a version history description with optional expandable details.
 *
 * Supports three states:
 * - Default: Simple text description
 * - Expandable (Collapsed): Clickable with chevron-right icon
 * - Expandable (Expanded): Shows chevron-down with details container, supporting line breaks
 *
 * @example
 * // Default type - simple text
 * <VersionHistoryDescription title="2 inputs modified" />
 *
 * @example
 * // Expandable type with details
 * <VersionHistoryDescription
 *   title="2 inputs modified"
 *   details={"Subject input added\nDescription input removed"}
 * />
 */
export function VersionHistoryDescription({
  title,
  details,
}: IVersionHistoryDescriptionProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  // Default type: no expandable functionality
  if (!details) {
    return (
      <Flex align="center" gap={1.5} data-testid="version-history-description">
        <Text
          level="small"
          color="muted"
          weight="normal"
          className="line-clamp-3"
          data-testid="version-history-description-text"
        >
          {title}
        </Text>
      </Flex>
    );
  }

  // Expandable type: with collapse/expand functionality
  return (
    <Collapsible
      open={isExpanded}
      onOpenChange={setIsExpanded}
      data-testid="version-history-description"
    >
      <Flex direction="column" gap={1.5} className="w-full">
        <CollapsibleTrigger asChild>
          <Flex
            role="button"
            tabIndex={0}
            align="center"
            gap={1.5}
            width="full"
            className="cursor-pointer"
            data-testid="version-history-description-trigger"
            onClick={e => e.stopPropagation()}
            onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                setIsExpanded(!isExpanded);
              }
            }}
          >
            <Flex align="center" className="shrink-0">
              <Icon
                type="ChevronRight"
                size="sm"
                className={cn(
                  'text-muted-foreground transition-transform duration-200',
                  isExpanded && 'rotate-90',
                )}
              />
            </Flex>
            <Text
              level="small"
              color="muted"
              weight="normal"
              className="flex-1 line-clamp-3"
              data-testid="version-history-description-text"
            >
              {title}
            </Text>
          </Flex>
        </CollapsibleTrigger>

        <CollapsibleContent className="overflow-hidden data-[state=open]:animate-collapsible-down data-[state=closed]:animate-collapsible-up">
          {details && (
            <Flex
              padding={2}
              width="full"
              data-testid="version-history-description-details"
            >
              <Text
                level="small"
                color="muted"
                weight="normal"
                className="whitespace-pre-line"
              >
                {details}
              </Text>
            </Flex>
          )}
        </CollapsibleContent>
      </Flex>
    </Collapsible>
  );
}
